import { useState, useEffect } from "react";
import "../../components/messagingWindow.css";
import "../../components/conversationEntry.css";
import "./awacDemo.css";

import RichComponentMessage from "../../components/richComponentMessage";
import GalleryPanel from "../../components/galleryPanel";
import MessagingInputFooter from "../../components/messagingInputFooter";
import MessagingHeader from "../../components/messagingHeader";
import { util } from "../../helpers/common";
import { CONVERSATION_CONSTANTS } from "../../helpers/constants";
import sampleEnvelope from "./awacEnvelope.sample.json";
import unitsEnvelope from "./awacUnitsEnvelope.sample.json";
import bookingEnvelope from "./awacBookingEnvelope.sample.json";
import slotsEnvelope from "./awacSlotsEnvelope.sample.json";
import confirmEnvelope from "./awacConfirmEnvelope.sample.json";

/**
 * AwacDemo
 * ---------------------------------------------------------------------------
 * Standalone harness that demonstrates the AWAC expand-and-render flow WITHOUT
 * a live Salesforce connection. It fabricates the exact conversation-entry
 * shape the Enhanced Chat API would deliver (a StaticContentMessage / Text
 * whose `text` is the AWAC JSON envelope) and feeds it through the SAME
 * RichComponentMessage renderer the production client uses.
 *
 * Reach it at:  /?demo=awac   (see src/App.js)
 *
 * This proves three things end-to-end in a browser:
 *   1. The client detects the envelope (isAwacMessage) and renders the
 *      FloorPlanShowcase template instead of a text bubble.
 *   2. The window expands when the component mounts (onAwacRender).
 *   3. The option buttons round-trip back to the agent (onAwacAction) — here
 *      we echo the reply as a new end-user text bubble.
 */

// Build a conversation-entry exactly as helpers/conversationEntryUtil expects it.
// `answeredQuestion` mirrors what conversation.js stamps from the visitor's
// pending message — it drives the question-aware feature bullets.
function makeAwacEntry(envelope, answeredQuestion) {
    return {
        conversationId: "demo-conversation",
        messageId: util.generateUUID(),
        entryType: CONVERSATION_CONSTANTS.EntryTypes.CONVERSATION_MESSAGE,
        content: {
            messageType: CONVERSATION_CONSTANTS.MessageTypes.STATIC_CONTENT_MESSAGE,
            staticContent: {
                formatType: CONVERSATION_CONSTANTS.FormatTypes.TEXT,
                // The envelope is delivered as the TEXT of a StaticContentMessage.
                text: JSON.stringify(envelope)
            }
        },
        actorType: CONVERSATION_CONSTANTS.ParticipantRoles.CHATBOT,
        actorName: "Lia",
        transcriptedTimestamp: Date.now(),
        answeredQuestion
    };
}

// Build a plain end-user text entry (used to echo a button reply).
function makeUserTextEntry(text) {
    return {
        conversationId: "demo-conversation",
        messageId: util.generateUUID(),
        entryType: CONVERSATION_CONSTANTS.EntryTypes.CONVERSATION_MESSAGE,
        content: {
            messageType: CONVERSATION_CONSTANTS.MessageTypes.STATIC_CONTENT_MESSAGE,
            staticContent: {
                formatType: CONVERSATION_CONSTANTS.FormatTypes.TEXT,
                text
            }
        },
        actorType: CONVERSATION_CONSTANTS.ParticipantRoles.ENDUSER,
        actorName: "You",
        transcriptedTimestamp: Date.now()
    };
}

export default function AwacDemo() {
    const [entries, setEntries] = useState([]);
    const [isExpanded, setIsExpanded] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [galleryEnvelope, setGalleryEnvelope] = useState(null);
    const [galleryTemplateName, setGalleryTemplateName] = useState(null);

    // Mirror conversation.js: broadcast the wing state so message rows adapt
    // (the "View matching units →" link hides while the gallery is open) —
    // the demo must exercise the exact production contract.
    useEffect(() => {
        window.dispatchEvent(new CustomEvent("awac:gallery-state", {
            detail: { open: !!galleryEnvelope, template: galleryEnvelope ? (galleryTemplateName || null) : null }
        }));
    }, [galleryEnvelope, galleryTemplateName]);

    function sendFloorPlans() {
        setEntries((prev) => [...prev, makeAwacEntry(sampleEnvelope)]);
    }

    function sendUnits() {
        // The complex-criteria question the sample envelope answers (real
        // scoring output for it) — exercises the question-aware bullets and
        // the premium-chip/bullet dedup exactly like production.
        setEntries((prev) => [...prev, makeAwacEntry(unitsEnvelope,
            "I love to cook, I want a big kitchen with a lot of counter space, "
            + "plus a flex room to work from, a 2-bedroom below $3,000")]);
    }

    function sendBooking() {
        setEntries((prev) => [...prev, makeAwacEntry(bookingEnvelope)]);
    }

    function sendSlots() {
        setEntries((prev) => [...prev, makeAwacEntry(slotsEnvelope)]);
    }

    function sendConfirm() {
        setEntries((prev) => [...prev, makeAwacEntry(confirmEnvelope)]);
    }

    function sendNeighborhood() {
        // No sample file needed: the template ships a mock project + POI set
        // it falls back to when the envelope carries no data.
        setEntries((prev) => [...prev, makeAwacEntry({
            awac: 1,
            text: "Here's what's around the project — tap a pin to see details, or ask me about anything nearby.",
            template: [{ name: "NeighborhoodMap" }]
        })]);
    }

    function sendPoiMapCommand() {
        // Mirrors the envelope BuildPoiMapCommandAwacService emits. In the real
        // app conversation.js intercepts this template and drives the
        // full-screen map page; in the demo the PoiMapControl fallback chip
        // renders and forwards the command to the poiMapBridge.
        setEntries((prev) => [...prev, makeAwacEntry({
            awac: 1,
            text: "Here's the closest grocery store — I've pulled it up on the neighborhood map.",
            template: [{ name: "PoiMapControl" }],
            options: { command: { intent: "showNearest", category: "Épiceries", name: "Costco", lang: "en" } }
        })]);
    }

    function reset() {
        setEntries([]);
        setIsExpanded(false);
        setGalleryEnvelope(null);
        setGalleryTemplateName(null);
    }

    // Mirrors conversation.js#handleAwacAction — sends a reply back to the agent.
    // In the demo we just echo it as an end-user bubble so the round-trip is visible.
    function handleAwacAction(text) {
        setEntries((prev) => [...prev, makeUserTextEntry(text)]);
        return Promise.resolve();
    }

    // Stub handlers so the real input footer renders in the demo.
    function demoSendTextMessage(conversationId, value) {
        setEntries((prev) => [...prev, makeUserTextEntry(value)]);
        return Promise.resolve();
    }

    function demoSendTypingIndicator() {
        return Promise.resolve();
    }

    // Mirrors conversation.js#handleAwacRender — expands the window and
    // mounts the gallery wing, exactly like the real client.
    function handleAwacRender(envelope, templateName) {
        setIsExpanded(true);
        setGalleryEnvelope(envelope);
        setGalleryTemplateName(templateName);
    }

    function handleCloseGallery() {
        setGalleryEnvelope(null);
        setGalleryTemplateName(null);
        setIsExpanded(false);
    }

    const windowClassName = `messagingWindow awacDemoWindow${isExpanded ? " awacExpanded" : ""}${isFullscreen ? " awacFullscreen" : ""}`;

    return (
        <div className="awacDemoPage">
            <header className="awacDemoBar">
                <div>
                    <strong>AWAC Demo</strong> — Adaptive Web Agent Component
                    <span className="awacDemoHint">
                        Simulates the agent emitting a FloorPlanShowcase envelope. No Salesforce connection.
                    </span>
                </div>
                <div className="awacDemoControls">
                    <button type="button" className="awacDemoPrimary" onClick={sendFloorPlans}>
                        Agent: show floor plans
                    </button>
                    <button type="button" className="awacDemoPrimary" onClick={sendUnits}>
                        Agent: show units
                    </button>
                    <button type="button" className="awacDemoPrimary" onClick={sendBooking}>
                        Agent: booking
                    </button>
                    <button type="button" className="awacDemoPrimary" onClick={sendSlots}>
                        Agent: slot picker
                    </button>
                    <button type="button" className="awacDemoPrimary" onClick={sendConfirm}>
                        Agent: confirm
                    </button>
                    <button type="button" className="awacDemoPrimary" onClick={sendNeighborhood}>
                        Agent: neighborhood
                    </button>
                    <button type="button" className="awacDemoPrimary" onClick={sendPoiMapCommand}>
                        Agent: map command
                    </button>
                    <button type="button" className="awacDemoGhost" onClick={reset}>
                        Reset
                    </button>
                </div>
            </header>

            <div className="awacDemoStage">
                <div className={windowClassName}>
                    {/* Mirrors conversation.js: gallery wing + chat rail in one row. */}
                    <div className="awacWidgetRow">
                        <div className="awacChatColumn">
                            {/* Real production header — exercises the booking
                                status chip and i18n exactly like the live client. */}
                            <MessagingHeader
                                conversationStatus={CONVERSATION_CONSTANTS.ConversationStatus.OPENED_CONVERSATION}
                                minimizeMessagingWindow={() => {}}
                                closeMessagingWindow={reset}
                                isFullscreen={isFullscreen}
                                onToggleFullscreen={() => setIsFullscreen((v) => !v)}
                                endConversation={reset} />

                            <div className="awacDemoBody">
                                {entries.length === 0 && (
                                    <div className="awacDemoEmpty">
                                        Click “Agent: show floor plans” to simulate the agent emitting an AWAC envelope.
                                    </div>
                                )}
                                {entries.map((entry) => {
                                    const isUser = entry.actorType === CONVERSATION_CONSTANTS.ParticipantRoles.ENDUSER;
                                    return (
                                        <div key={entry.messageId} className="conversationEntryContainer">
                                            {isUser ? (
                                                <div className="awacDemoUserBubble">{entry.content.staticContent.text}</div>
                                            ) : (
                                                <RichComponentMessage
                                                    conversationEntry={entry}
                                                    onAwacAction={handleAwacAction}
                                                    onAwacRender={handleAwacRender} />
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            <MessagingInputFooter
                                conversationStatus={CONVERSATION_CONSTANTS.ConversationStatus.OPENED_CONVERSATION}
                                sendTextMessage={demoSendTextMessage}
                                sendTypingIndicator={demoSendTypingIndicator} />
                        </div>
                        {
                            galleryEnvelope &&
                            <GalleryPanel
                                envelope={galleryEnvelope}
                                templateName={galleryTemplateName}
                                onClose={handleCloseGallery}
                                onAction={(action) => handleAwacAction(action && action.unitName ? `I'd like to book a visit for ${action.unitName}` : String(action))} />
                        }
                    </div>
                </div>
            </div>
        </div>
    );
}
